// @ts-check

/** @file Displays and manages a Task's Board placements across Projects. */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import Text from "@kaspernj/api-maker/build/utils/text"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {Link} from "expo-router"
import {memo, useMemo} from "react"
import {ActivityIndicator} from "react-native"
import {View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import useLiveQuery from "velocious/build/src/database/use-live-query.js"

import ActionMenu from "../action-menu"
import Button from "../button"
import Card from "../card"
import ModelSelect from "../model-select"
import confirmModal from "../../shared/confirm-modal"
import errorMessage from "../../shared/error-message"
import buildRoutePath from "../../shared/build-route-path"
import {TaskBoard as RemoteTaskBoard} from "../../frontend-models/task-board"
import {projectReplicaRowsSource} from "../../database/project-replica-reads"
import {reconcileTaskBoardPlaceResponse, reconcileTaskBoardRemoveResponse} from "../../database/project-replica-task-board-commands"
import BoardColumn from "../../models/board-column"
import Board from "../../models/board"
import Project from "../../models/project"
import TaskBoard from "../../models/task-board"
import useCurrentUser from "../../auth/use-current-user"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import {useProjectSyncOnline} from "../../sync/project-sync-connectivity"
import {projectSyncLifecycle, useActiveProjectReplica} from "../../sync/project-sync-lifecycle"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import {ActionMenuItem} from "../action-menu" */
/** @import {TaskBoardAvailableBoardsResponse, TaskBoardRemoveFromBoardResponse} from "../../../../shared/task-board-placement-command-types.js" */
/** @import {OpenProjectReplica} from "../../database/project-replica.js" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TaskBoardPlacementsProps
 * @property {string} [currentBoardId] Board whose removal should close a surrounding Board modal.
 * @property {(response: TaskBoardRemoveFromBoardResponse) => unknown} [onCurrentBoardPlacementRemoved] Current-Board removal callback.
 * @property {boolean} [online] Whether online-only placement commands are allowed.
 * @property {string} projectId Owning project replica id.
 * @property {{id: () => string | number, userId: () => string | null}} task Task whose placements are displayed.
 * @property {Record<string, string | string[] | undefined>} [params] Router query params for route building.
 * @property {string} [testID] Base test id.
 */

/**
 * @typedef {object} TaskBoardPlacementsState
 * @property {boolean} adding Whether a placement command is running.
 * @property {string | null} menuBoardId Board selected in the placement menu.
 * @property {string | null} removingBoardId Board currently being removed.
 * @property {string} selectedBoardId Current picker selection.
 * @property {string | null} statusError Placement-section error message.
 */

/**
 * Displays independently loaded Board placements grouped by Project.
 * @augments {ShapeComponent<TaskBoardPlacementsProps, TaskBoardPlacementsState>}
 */
class TaskBoardPlacements extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** @type {ReturnType<typeof useCurrentUser>} */
  currentUser = this.hookField()

  static propTypes = PropTypesExact({
    currentBoardId: PropTypes.string,
    onCurrentBoardPlacementRemoved: PropTypes.func,
    online: PropTypes.bool,
    params: PropTypes.object,
    projectId: PropTypes.string.isRequired,
    task: PropTypes.object.isRequired,
    testID: PropTypes.string
  })

  static defaultProps = {
    currentBoardId: undefined,
    onCurrentBoardPlacementRemoved: undefined,
    online: undefined,
    params: undefined,
    testID: "taskBoardPlacements"
  }

  /** @type {TaskBoardPlacementsState} */
  state = {
    adding: false,
    menuBoardId: null,
    removingBoardId: null,
    selectedBoardId: "",
    statusError: null
  }

  /** @type {TaskBoardPlacementView[]} */
  placements = []
  loading = true
  readError = /** @type {Error | null} */ (null)
  online = true
  /** @type {OpenProjectReplica | null} */
  replica = null

  /** Declares placement reads against the exact project replica. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.currentUser = useCurrentUser()
    this.themeName = useThemeName()
    const projectId = this.p.projectId
    const taskId = String(this.p.task.id())
    const replica = useActiveProjectReplica(projectId)
    const placementsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: TaskBoard,
      projectId,
      query: (taskBoards) => taskBoards
        .ransack({taskIdEq: taskId})
        .order({column: "board_id", direction: "ASC"})
        .order({column: "row_number", direction: "ASC"})
        .order({column: "id", direction: "ASC"}),
      replica
    }), [projectId, replica, taskId])
    const placementsState = useLiveQuery(placementsSource)
    const boardIdsKey = [...new Set(placementsState.results.map((placement) => placement.boardId()).filter(Boolean))].sort().join(",")
    const columnIdsKey = [...new Set(placementsState.results.map((placement) => placement.boardColumnId()).filter(Boolean))].sort().join(",")
    const boardsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: Board,
      projectId,
      query: (boards) => boardIdsKey ? boards.where({id: boardIdsKey.split(",")}) : boards.where({id: []}),
      replica
    }), [boardIdsKey, projectId, replica])
    const columnsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: BoardColumn,
      projectId,
      query: (columns) => columnIdsKey ? columns.where({id: columnIdsKey.split(",")}) : columns.where({id: []}),
      replica
    }), [columnIdsKey, projectId, replica])
    const boardsState = useLiveQuery(boardsSource)
    const columnsState = useLiveQuery(columnsSource)
    const boardsById = recordsById(boardsState.results)
    const placementProjectIdsKey = [...new Set(placementsState.results.map((placement) => (
      placement.boardProjectId() || boardsById[String(placement.boardId())]?.projectId()
    )).filter(Boolean))].sort().join(",")
    const projectQuery = useMemo(
      () => placementProjectIdsKey ? Project.where({id: placementProjectIdsKey.split(",")}) : Project.where({id: []}),
      [placementProjectIdsKey]
    )
    const projectState = useLiveQuery(projectQuery)
    const columnsById = recordsById(columnsState.results)
    const projectsById = recordsById(projectState.results)
    const queryStates = [placementsState, boardsState, columnsState, projectState]

    const syncOnline = useProjectSyncOnline()

    this.placements = sortedPlacements(placementsState.results.map((placement) => {
      const board = boardsById[String(placement.boardId())] || null
      const boardProjectId = placement.boardProjectId() || board?.projectId()

      return new TaskBoardPlacementView({
        board,
        boardColumn: columnsById[String(placement.boardColumnId())] || null,
        placement,
        project: boardProjectId ? projectsById[String(boardProjectId)] || null : null
      })
    }))
    this.loading = !replica || queryStates.some((state) => state.loading)
    this.readError = queryStates.find((state) => state.error)?.error || null
    this.online = this.p.online ?? syncOnline
    this.replica = replica
  }

  /**
   * Renders the grouped placement section.
   * @returns {ReactNode} Grouped placement section.
   */
  render() {
    const testID = this.p.testID || "taskBoardPlacements"
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    const placements = this.placements
    const loading = this.loading
    const statusError = this.s.statusError || this.readError?.message || null
    const groups = groupedPlacements(placements)
    const menuPlacement = this.s.menuBoardId
      ? placements.find((placement) => placement.boardId() === this.s.menuBoardId) || null
      : null

    return (
      <Card
        style={styles.section ||= {gap: 14, marginTop: 16}}
        testID={testID}
      >
        <Text
          style={styles[`title-${themeName}`] ||= {color: colors.text, fontSize: 18, fontWeight: 700}}
          testID={`${testID}/title`}
        >
          {_("Boards")}
        </Text>
        {this.tt.canManagePlacements() && this.online &&
          <View testID={`${testID}/add`}>
            <ModelSelect
              closeOnChange
              label={_("Add to Board")}
              onChangeValue={this.tt.onBoardSelect}
              options={this.tt.loadBoardOptions}
              placeholder={_("Search projects and Boards...")}
              testID={`${testID}/boardSelect`}
              values={this.s.selectedBoardId ? [this.s.selectedBoardId] : []}
            />
            {this.s.adding &&
              <Text
                style={styles[`progressText-${themeName}`] ||= {color: colors.textMuted, marginTop: 8}}
                testID={`${testID}/adding`}
              >
                {_("Adding Board...")}
              </Text>
            }
          </View>
        }
        {statusError &&
          <View
            style={styles.error ||= {alignItems: "flex-start", gap: 8}}
            testID={`${testID}/error`}
          >
            <Text
              style={styles[`errorText-${themeName}`] ||= {color: colors.dangerText, fontWeight: 600}}
              testID={`${testID}/errorText`}
            >
              {statusError}
            </Text>
            <Button
              iconName="rotate-right"
              label={_("Retry")}
              onPress={this.tt.onRetryPress}
              testID={`${testID}/retryButton`}
              variant="outline"
            />
          </View>
        }
        {loading && placements.length === 0 &&
          <View
            style={styles.loading ||= {alignItems: "center", flexDirection: "row", gap: 8}}
            testID={`${testID}/loading`}
          >
            <ActivityIndicator color={colors.link} size="small" />
            <Text style={styles[`progressText-${themeName}`]}>{_("Loading Boards...")}</Text>
          </View>
        }
        {!loading && placements.length === 0 && !statusError &&
          <Text
            style={styles[`empty-${themeName}`] ||= {color: colors.textMuted}}
            testID={`${testID}/empty`}
          >
            {_("This task is not on any Boards.")}
          </Text>
        }
        {groups.map((group) =>
          <View
            key={group.projectId}
            style={styles.group ||= {gap: 8}}
            testID={`${testID}/project/${group.projectId}`}
          >
            <Text
              style={styles[`projectName-${themeName}`] ||= {color: colors.text, fontSize: 16, fontWeight: 700}}
              testID={`${testID}/project/${group.projectId}/name`}
            >
              {group.projectName}
            </Text>
            {group.placements.map((placement) =>
              <TaskBoardPlacementRowComponent
                canManage={this.tt.canManagePlacements() && this.online}
                key={placement.id()}
                onMenuPress={this.tt.onPlacementMenuPress}
                params={this.p.params}
                placement={placement}
                testID={`${testID}/placement/${placement.boardId()}`}
              />
            )}
          </View>
        )}
        <ActionMenu
          isVisible={Boolean(menuPlacement)}
          items={this.tt.placementMenuItems()}
          onClosePress={this.tt.onPlacementMenuClosePress}
          testID={`${testID}/placementMenu`}
          title={_("Board placement")}
        />
      </Card>
    )
  }

  /**
   * Checks whether the signed-in actor may manage placements.
   * @returns {boolean} Whether the signed-in actor owns the Task or is an admin.
   */
  canManagePlacements() {
    const user = this.tt.currentUser.user

    return Boolean(user && (user.admin || String(user.id) === String(this.p.task.userId())))
  }

  /**
   * Loads one bounded page of authorized, unplaced Board options.
   * @param {{page?: number, searchValue?: string}} [args] HayaSelect loader args.
   * @returns {Promise<TaskBoardAvailableBoardsResponse>} Board option page.
   * @rejects {unknown} When the Board option request fails.
   */
  loadBoardOptions = async ({page = 1, searchValue = ""} = {}) => {
    return await RemoteTaskBoard.availableBoardsForTask({
      page,
      searchValue,
      taskId: String(this.p.task.id())
    })
  }

  /**
   * Adds the task to the selected Board.
   * @param {unknown} value Selected Board id.
   * @returns {void} Nothing.
   */
  onBoardSelect = (value) => {
    if (typeof value !== "string" || value.length === 0 || this.s.adding) return
    void this.tt.addToBoard(value)
  }

  /**
   * Places the task on a Board and refreshes the displayed placements.
   * @param {string} boardId Destination Board id.
   * @returns {Promise<void>} Resolves after add handling.
   */
  async addToBoard(boardId) {
    if (!this.online) {
      this.s.statusError = _("This action is only available while online")
      return
    }
    this.s.adding = true
    this.s.selectedBoardId = boardId
    this.s.statusError = null

    try {
      const replica = this.replica
      if (!replica) throw new Error("Cannot add a Board placement without its project replica")
      const response = await RemoteTaskBoard.placeOnBoard({boardId, taskId: String(this.p.task.id())})

      await reconcileTaskBoardPlaceResponse({projectId: this.p.projectId, replica, response})
      this.s.selectedBoardId = ""
    } catch (error) {
      this.s.selectedBoardId = ""
      this.s.statusError = errorMessage(error)
    } finally {
      this.s.adding = false
    }
  }

  /**
   * Retries the placement query.
   * @returns {void} Nothing.
   */
  onRetryPress = () => {
    void projectSyncLifecycle.retry().catch((error) => {
      this.s.statusError = errorMessage(error)
    })
  }

  /**
   * Opens the action menu for one placement.
   * @param {TaskBoardPlacementView} placement Placement whose menu should open.
   * @returns {void} Nothing.
   */
  onPlacementMenuPress = (placement) => {
    this.s.menuBoardId = placement.boardId()
  }

  /**
   * Closes the placement action menu.
   * @returns {void} Nothing.
   */
  onPlacementMenuClosePress = () => {
    this.s.menuBoardId = null
  }

  /**
   * Builds the placement menu actions.
   * @returns {ActionMenuItem[]} Placement menu actions.
   */
  placementMenuItems() {
    const removing = Boolean(this.s.menuBoardId && this.s.removingBoardId === this.s.menuBoardId)

    return [{
      destructive: true,
      disabled: !this.online || removing,
      iconName: "trash",
      label: removing ? _("Removing...") : _("Remove from Board"),
      onPress: this.tt.onRemoveMenuPress,
      testID: `${this.p.testID}/removeButton`
    }]
  }

  /**
   * Confirms and removes exactly one Board placement.
   * @returns {Promise<void>} Resolves after removal handling.
   * @rejects {unknown} When confirmation or the current-Board callback fails.
   */
  onRemoveMenuPress = async () => {
    if (!this.online) {
      this.s.statusError = _("This action is only available while online")
      return
    }
    const boardId = this.s.menuBoardId
    if (!boardId) return

    const boardName = this.placements.find((placement) => placement.boardId() === boardId)?.boardName() || _("Board")
    this.s.menuBoardId = null
    const confirmed = await confirmModal(_("Remove this task from %{boardName}?", {boardName}), {
      cancelLabel: _("Cancel"),
      confirmLabel: _("Remove"),
      title: _("Remove Board placement"),
      tone: "destructive"
    })
    if (!confirmed) return

    this.s.removingBoardId = boardId
    this.s.statusError = null
    try {
      const replica = this.replica
      if (!replica) throw new Error("Cannot remove a Board placement without its project replica")
      const response = await RemoteTaskBoard.removeFromBoard({boardId, taskId: String(this.p.task.id())})
      await reconcileTaskBoardRemoveResponse({projectId: this.p.projectId, replica, response})
      if (this.p.currentBoardId === boardId) {
        await this.p.onCurrentBoardPlacementRemoved?.(response)
      }
    } catch (error) {
      this.s.statusError = errorMessage(error)
    } finally {
      this.s.removingBoardId = null
    }
  }
}

/** @typedef {{canManage: boolean, onMenuPress: (placement: TaskBoardPlacementView) => unknown, params?: Record<string, string | string[] | undefined>, placement: TaskBoardPlacementView, testID: string}} TaskBoardPlacementRowProps */
/** @typedef {Record<string, never>} TaskBoardPlacementRowState */

/**
 * Renders one Board placement with a stable menu handler.
 * @augments {ShapeComponent<TaskBoardPlacementRowProps, TaskBoardPlacementRowState>}
 */
class TaskBoardPlacementRow extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  static propTypes = PropTypesExact({
    canManage: PropTypes.bool.isRequired,
    onMenuPress: PropTypes.func.isRequired,
    params: PropTypes.object,
    placement: PropTypes.object.isRequired,
    testID: PropTypes.string.isRequired
  })

  static defaultProps = {
    params: undefined
  }

  /** Initializes theme state. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders one Board and BoardColumn row.
   * @returns {ReactNode} One Board and BoardColumn row.
   */
  render() {
    const colors = this.tt.colors
    const themeName = this.tt.themeName

    return (
      <View
        style={styles[`placement-${themeName}`] ||= {
          alignItems: "center",
          borderColor: colors.divider,
          borderRadius: 10,
          borderWidth: 1,
          flexDirection: "row",
          gap: 12,
          justifyContent: "space-between",
          padding: 10
        }}
        testID={this.p.testID}
      >
        <View style={styles.placementText ||= {flex: 1, gap: 2}}>
          <Link
            href={buildRoutePath(this.p.params, `/projects/${this.p.placement.boardProjectId()}/boards/${this.p.placement.boardId()}`)}
            style={styles[`detailValueLink-${themeName}`] ||= {
              color: colors.link,
              fontSize: 16,
              fontWeight: 600,
              textDecorationLine: "underline"
            }}
            testID={`${this.p.testID}/boardLink`}
          >
            <Text
              accessible
              accessibilityLabel={this.p.placement.boardName() || _("Board")}
              style={styles[`boardName-${themeName}`] ||= {color: colors.text, fontWeight: 600}}
              testID={`${this.p.testID}/board`}
            >
              {this.p.placement.boardName() || _("Board")}
            </Text>
          </Link>
          <Text
            style={styles[`columnName-${themeName}`] ||= {color: colors.textMuted, fontSize: 13}}
            testID={`${this.p.testID}/column`}
          >
            {_("Column: %{columnName}", {columnName: this.p.placement.boardColumnName() || _("Column")})}
          </Text>
        </View>
        {this.p.canManage &&
          <Button
            iconName="ellipsis"
            label={_("Actions")}
            onPress={this.tt.onMenuPress}
            testID={`${this.p.testID}/menuButton`}
            variant="outline"
          />
        }
      </View>
    )
  }

  /**
   * Opens the menu for this placement.
   * @returns {void} Nothing.
   */
  onMenuPress = () => {
    this.p.onMenuPress(this.p.placement)
  }
}

/** @type {NamedExoticComponent<TaskBoardPlacementRowProps>} */
const TaskBoardPlacementRowComponent = memo(shapeComponent(TaskBoardPlacementRow))

/** @type {NamedExoticComponent<TaskBoardPlacementsProps>} */
const TaskBoardPlacementsComponent = memo(shapeComponent(TaskBoardPlacements))

export default TaskBoardPlacementsComponent

/** Immutable presentation relationship over tenant-local placement rows. */
class TaskBoardPlacementView {
  /**
   * Captures the local graph resolved inside one project handle.
   * @param {{board: InstanceType<typeof Board> | null, boardColumn: InstanceType<typeof BoardColumn> | null, placement: InstanceType<typeof TaskBoard>, project: InstanceType<typeof Project> | null}} args Local graph.
   */
  constructor({board, boardColumn, placement, project}) {
    this.localBoard = board
    this.localBoardColumn = boardColumn
    this.localPlacement = placement
    this.localProject = project
    Object.freeze(this)
  }

  /**
   * Reads the board captured with this view.
   * @returns {InstanceType<typeof Board> | null} Local board row.
   */
  board() { return this.localBoard }

  /**
   * Reads the best available Board label without crossing replica handles.
   * @returns {string | null} Board label from the authoritative placement snapshot or local relationship.
   */
  boardName() { return this.localBoard?.name() || this.localPlacement.boardName() || null }

  /**
   * Reads the destination project captured with this placement.
   * @returns {string | null} Destination project id from the snapshot or local relationship.
   */
  boardProjectId() { return this.localBoard?.projectId() || this.localPlacement.boardProjectId() || null }

  /**
   * Reads the board column captured with this view.
   * @returns {InstanceType<typeof BoardColumn> | null} Local board-column row.
   */
  boardColumn() { return this.localBoardColumn }

  /**
   * Reads the best available BoardColumn label without crossing replica handles.
   * @returns {string | null} BoardColumn label from the authoritative placement snapshot or local relationship.
   */
  boardColumnName() { return this.localBoardColumn?.name() || this.localPlacement.boardColumnName() || null }

  /**
   * Reads the placement's board foreign key.
   * @returns {string} Owning board id.
   */
  boardId() { return this.localPlacement.boardId() }

  /**
   * Reads the durable identity used by rendered lists.
   * @returns {string} Stable placement identifier.
   */
  id() { return this.localPlacement.id() }

  /**
   * Reads the catalog project captured with this view.
   * @returns {InstanceType<typeof Project> | null} Cached catalog project.
   */
  project() { return this.localProject }
}

/**
 * Builds a durable-id lookup for local records.
 * @template {{id: () => string}} T - Local record type.
 * @param {T[]} records Local rows.
 * @returns {Record<string, T>} Rows keyed by id.
 */
function recordsById(records) {
  return Object.fromEntries(records.map((record) => [String(record.id()), record]))
}

/**
 * Sorts placements by Project, Board, BoardColumn, then placement id.
 * @param {TaskBoardPlacementView[]} placements Tenant-local placement views.
 * @returns {TaskBoardPlacementView[]} Sorted copy.
 */
function sortedPlacements(placements) {
  return placements.slice().sort((left, right) => {
    const leftBoard = left.board()
    const rightBoard = right.board()
    const leftProject = left.project()
    const rightProject = right.project()

    return String(leftProject?.name() || "").localeCompare(String(rightProject?.name() || "")) ||
      String(leftBoard?.name() || "").localeCompare(String(rightBoard?.name() || "")) ||
      String(left.boardColumn()?.name() || "").localeCompare(String(right.boardColumn()?.name() || "")) ||
      left.id().localeCompare(right.id())
  })
}

/**
 * Groups sorted placements by Project while retaining each Board row.
 * @param {TaskBoardPlacementView[]} placements Sorted placements.
 * @returns {{placements: TaskBoardPlacementView[], projectId: string, projectName: string}[]} Project groups.
 */
function groupedPlacements(placements) {
  /** @type {Map<string, {placements: TaskBoardPlacementView[], projectId: string, projectName: string}>} */
  const groups = new Map()

  for (const placement of placements) {
    const project = placement.project()
    if (!project) continue
    const projectId = project.id()
    let group = groups.get(projectId)

    if (!group) {
      group = {placements: [], projectId, projectName: project.name() || _("Project")}
      groups.set(projectId, group)
    }
    group.placements.push(placement)
  }

  return [...groups.values()]
}
